import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from '../app/api';

const initialState = {
    insurances: [],
    insuranceType: null,
    loading: false,
    error: null,
    freelanceIncome: [],
    freelanceLoading: false,
    freelanceError: null,
}

export const addInsuranceIncome = createAsyncThunk(
    'insuranceIncome/addInsuranceIncome',
    async ({ monthlyData, year, company_id }, { rejectWithValue }) => {
        try {
            const response = await api.post('/insuranceIncome/add', { monthlyData, year, company_id });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'Грешка при добавяне на осигуровки');
        }
    }
);

export const updateInsuranceIncome = createAsyncThunk(
    'insuranceIncome/updateInsuranceIncome',
    async ({ id, monthlyData, year, company_id }, { rejectWithValue}) => {
        try {
            const response = await api.put(`/insuranceIncome/update/${id}`, { monthlyData, year, company_id})
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'Грешка при обновяване на осигуровки');
        }
    }
);

export const deleteInsuranceIncome = createAsyncThunk(
    'insuranceIncome/deleteInsuranceIncome',
    async ({ id, year, company_id }, { rejectWithValue }) => {
        try {
            const response = await api.delete(`/insuranceIncome/delete/${id}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'Грешка при изтриване на осигурителен доход');
        }
    }
);

export const getInsuranceIncomeById = createAsyncThunk(
    'insuranceIncome/getInsuranceIncomeById',
    async ({ id }, { rejectWithValue }) => {
        try {
            const response = await api.get(`/insuranceIncome/get/${id}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'Грешка при взимането на осигурителен доход по id');
        }
    }
);

export const getAllInsuranceIncome = createAsyncThunk(
    'insuranceIncome/getAllInsuranceIncome',
    async ({ company_id }, { rejectWithValue }) => {
        try {
            const response = await api.get(`/insuranceIncome/all`, { params: { company_id } });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'Грешка при взимането на осигурителните доходи');
        }
    }
);

export const getInsuranceIncomeByYear = createAsyncThunk(
    'insuranceIncome/getInsuranceIncomeByYear',
    async ({ year, company_id }, { rejectWithValue }) => {
        try {
            const response = await api.get(`/insuranceIncome/by-year`, { params: { year, company_id } });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'Грешка при взимането на осигурителен доход за дадена година');
        }
    }
);

export const changeInsuranceType = createAsyncThunk(
    'insuranceIncome/changeInsuranceType',
    async ({ insuranceType, company_id, freelancer, otherIncome }, { rejectWithValue }) => {
        try {
            const response = await api.put(`/insuranceIncome/change-insurance-type/${company_id}`, { insuranceType, freelancer, otherIncome });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'Грешка при промяна на тип осигурителен доход');
        }
    }
);

export const getInsuranceType = createAsyncThunk(
    'insuranceIncome/getInsuranceType',
    async ({ company_id }, { rejectWithValue }) => {
        try {
            const response = await api.get(`/insuranceIncome/get-insurance-type`, { params: { company_id }});
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'Грешка при взимането на типа осигуровки');
        }
    }
);

export const addFreelanceIncome = createAsyncThunk(
    'insuranceIncome/addFreelanceIncome',
    async ({ monthlyData, year, company_id }, { rejectWithValue }) => {
        try {
            const response = await api.post('/insuranceIncome/freelanceIncome/add', { monthlyData, year, company_id });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'Грешка при добавяне на доход от личен труд');
        }
    }
);

export const updateFreelanceIncome = createAsyncThunk(
    'insuranceIncome/updateFreelanceIncome',
    async ({ id, monthlyData, year, company_id }, { rejectWithValue}) => {
        try {
            const response = await api.put(`/freelanceIncome/update/${id}`, { monthlyData, year, company_id})
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'Грешка при обновяване на доход от личен труд');
        }
    }
);

export const getAllFreelanceIncome = createAsyncThunk(
    'insuranceIncome/getAllFreelanceIncome',
    async ({ company_id }, { rejectWithValue }) => {
        try {
            const response = await api.get(`/insuranceIncome/freelanceIncome/all`, { params: { company_id } });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'Грешка при взимането на доходите от личен труд');
        }
    }
);

export const deleteFreelanceIncome = createAsyncThunk(
    'insuranceIncome/deleteFreelanceIncome',
    async ({ id, year, company_id }, { rejectWithValue }) => {
        try {
            const response = await api.delete(`/freelanceIncome/delete/${id}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.message || 'Грешка при изтриване на доход от личен труд');
        }
    }
);


const insuranceIncomeSlice = createSlice({
    name: 'insuranceIncome',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearFreelanceError: (state) => {
            state.freelanceError = null;
        }
    },
    extraReducers: (builder) => {
        builder
        .addCase(addInsuranceIncome.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(addInsuranceIncome.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null;
            // Add the new records to the state
            if (action.payload.records) {
                state.insurances.push(...action.payload.records);
            }
        })
        .addCase(addInsuranceIncome.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(updateInsuranceIncome.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(updateInsuranceIncome.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null;
            // Remove old records for this year and add new ones
            if (action.payload.records) {
                const year = action.payload.records[0]?.year;
                if (year) {
                    state.insurances = state.insurances.filter(insurance => insurance.year !== year);
                    state.insurances.push(...action.payload.records);
                }
            }
        })
        .addCase(updateInsuranceIncome.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(deleteInsuranceIncome.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(deleteInsuranceIncome.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null;
            state.insurances = state.insurances.filter(insurance => insurance.id !== action.payload.id);
        })
        .addCase(deleteInsuranceIncome.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(getInsuranceIncomeById.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(getInsuranceIncomeById.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null;
            state.insurances = [action.payload];
        })
        .addCase(getInsuranceIncomeById.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(getAllInsuranceIncome.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(getAllInsuranceIncome.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null;
            state.insurances = action.payload;
        })
        .addCase(getAllInsuranceIncome.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(getInsuranceIncomeByYear.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(getInsuranceIncomeByYear.fulfilled, (state, action) =>{
            state.loading = false;
            state.error = null;
            // Handle the response properly - it could be an array or empty
            if (action.payload && Array.isArray(action.payload) && action.payload.length > 0) {
                // Merge the year-specific data with existing data
                const year = action.payload[0]?.year;
                if (year) {
                    const existingData = state.insurances.filter(insurance => insurance.year !== year);
                    state.insurances = [...existingData, ...action.payload];
                }
            } else {
                // No data for this year, which is fine
                console.log('No insurance income data found for the selected year');
            }
        })
        .addCase(getInsuranceIncomeByYear.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(changeInsuranceType.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(changeInsuranceType.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null;
            state.insuranceType = action.payload.insuranceType;
        })
        .addCase(changeInsuranceType.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        .addCase(getInsuranceType.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(getInsuranceType.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null;
            state.insuranceType = action.payload;
        })
        .addCase(getInsuranceType.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload.insuranceType;
        })
        // Freelance Income reducers
        .addCase(addFreelanceIncome.pending, (state) => {
            state.freelanceLoading = true;
            state.freelanceError = null;
        })
        .addCase(addFreelanceIncome.fulfilled, (state, action) => {
            state.freelanceLoading = false;
            state.freelanceError = null;
            // Add the new records to the state
            if (action.payload.records) {
                state.freelanceIncome.push(...action.payload.records);
            }
        })
        .addCase(addFreelanceIncome.rejected, (state, action) => {
            state.freelanceLoading = false;
            state.freelanceError = action.payload;
        })
        .addCase(updateFreelanceIncome.pending, (state) => {
            state.freelanceLoading = true;
            state.freelanceError = null;
        })
        .addCase(updateFreelanceIncome.fulfilled, (state, action) => {
            state.freelanceLoading = false;
            state.freelanceError = null;
            // Remove old records for this year and add new ones
            if (action.payload.records) {
                const year = action.payload.records[0]?.year;
                if (year) {
                    state.freelanceIncome = state.freelanceIncome.filter(income => income.year !== year);
                    state.freelanceIncome.push(...action.payload.records);
                }
            }
        })
        .addCase(updateFreelanceIncome.rejected, (state, action) => {
            state.freelanceLoading = false;
            state.freelanceError = action.payload;
        })
        .addCase(getAllFreelanceIncome.pending, (state) => {
            state.freelanceLoading = true;
            state.freelanceError = null;
        })
        .addCase(getAllFreelanceIncome.fulfilled, (state, action) => {
            state.freelanceLoading = false;
            state.freelanceError = null;
            state.freelanceIncome = action.payload;
        })
        .addCase(getAllFreelanceIncome.rejected, (state, action) => {
            state.freelanceLoading = false;
            state.freelanceError = action.payload;
        })
        .addCase(deleteFreelanceIncome.pending, (state) => {
            state.freelanceLoading = true;
            state.freelanceError = null;
        })
        .addCase(deleteFreelanceIncome.fulfilled, (state, action) => {
            state.freelanceLoading = false;
            state.freelanceError = null;
            state.freelanceIncome = state.freelanceIncome.filter(income => income.id !== action.payload.id);
        })
        .addCase(deleteFreelanceIncome.rejected, (state, action) => {
            state.freelanceLoading = false;
            state.freelanceError = action.payload;
        })
    }
})

export default insuranceIncomeSlice.reducer;
    